// tiptap extensions
import Document from "@tiptap/extension-document";
import Link from "@tiptap/extension-link";
import StarterKit from "@tiptap/starter-kit";
import HardBreak from "@tiptap/extension-hard-break";
import { lowlight } from "lowlight/lib/common.js";
// custom extensions
import { Heading, Title } from "./extensions/Heading/Heading";
import { IframeEmbed } from "./extensions/Embeds/embeds";
import { TextColor } from "./extensions/ColorMenu/TextColorMark";
import { Highlight } from "./extensions/ColorMenu/TextHighlightMark";
// custom blocks
import { ImageBlock } from "./extensions/blocks/Image/ImageBlock";
import { FileBlock } from "./extensions/blocks/File/FileBlock";
import MemberInlineBlock from "./extensions/blocks/Members/MemberInlineBlock";
import { ToolInlineBlock } from "./extensions/blocks/Tool/ToolInlineBlock";
import SpaceInlineBlock from "./extensions/blocks/Space/SpaceInlineBlock";
import GifBlock from "./extensions/blocks/Gif/GifBlock";
import { OpenGraphBlock } from "./extensions/blocks/OpenGraph/OpenGraphBlock";
import ThreadBlock from "./extensions/blocks/Thread/ThreadBlock";
import Contents from "./extensions/Contents/Contents";
import ReplyBlock from "$components/editor/extensions/blocks/Reply/ReplyBlock";
import CalloutBlock from "$components/editor/extensions/blocks/Callout/CalloutBlock";
import DocsGalleryBlock from "./extensions/blocks/DocsGallery/DocsGalleryBlock";
import { VideoBlock } from "./extensions/blocks/Video/VideoBlock";
import {
  AccordionBlock,
  AccordionSummary,
  AccordionContent,
} from "$components/editor/extensions/blocks/Accordion/AccordionBlock";
import { SplineEmbed } from "./extensions/spline/embeds";
import TabsPanel from "./extensions/blocks/Tabs/TabsPanel";
import TabNode from "./extensions/blocks/Tabs/TabNode";
import TableBlock from "./extensions/blocks/Table/TableBlock";
import TableHeader from "./extensions/blocks/Table/TableHeader";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "./extensions/blocks/Table/TableCell";
import FileUploadPlugin from "./extensions/uploadFiles";
import LinkHandle from "./extensions/Heading/LinkHandle";
import Paragraph from "@tiptap/extension-paragraph";
import { YChange, calcYchangeDomAttrs, hoverWrapper } from "./extensions/Collaboration/YChange";
import { HorizontalRule } from "./extensions/blocks/HorizontalRule/HorizontalRule";
import { Blockquote } from "./extensions/blocks/Blockquote/Blockquote";
import { CodeBlock } from "./extensions/blocks/CodeBlock/CodeBlock";
import { Markdown } from "tiptap-markdown";

const DiffParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ychange: {
        default: null,
      },
    };
  },
  renderHTML({ node }) {
    const renderChanges = node.content.size === 0;
    const attrs = renderChanges ? calcYchangeDomAttrs(node.attrs) : node.attrs;
    const defChildren = [0];
    const children = renderChanges ? hoverWrapper(node.attrs.ychange, defChildren) : defChildren;
    return ["p", attrs, ...children];
  },
});

const NormalDocument = Document.extend({ content: "(block+|tableOfContents)" });
const HeadedDocument = Document.extend({
  content: "(title block+|tableOfContents)",
});
const DisabledIframe = IframeEmbed.extend({
  renderHTML: ({ HTMLAttributes }) => {
    // there's a strange bug when rendering iframe embeds when using generateHTML
    // so we just render a span instead for SSR in these cases
    return ["span", HTMLAttributes];
  },
});
const DisabledLink = Link.extend({
  renderHTML: ({ HTMLAttributes }) => {
    // there's a strange bug when rendering editor links inside of an anchor tag when using generateHTML
    // so we just render a span instead for SSR in these cases where the link shouldn't be clicked
    return ["span", HTMLAttributes, 0];
  },
});

export const readOnlyExtensions = (
  collab: boolean,
  forceHeading: boolean,
  highlightColor: string,
  disableLinkHandle: boolean = false,
  disableLink: boolean = false,
  isSSR: boolean = false
) => [
  forceHeading ? HeadedDocument : NormalDocument,
  StarterKit.configure({
    document: false,
    // collab plugin manages history itself
    history: collab ? false : undefined,
    dropcursor: {
      color: "#6B7280",
      width: 2,
    },
    heading: false,
    paragraph: false,
    hardBreak: false,
    codeBlock: false,
    blockquote: false,
    horizontalRule: false,
    bulletList: {
      HTMLAttributes: {
        class: "marker:text-neutral-600 marker:dark:text-neutral-300",
      },
    },
    code: {
      HTMLAttributes: {
        class:
          "border border-neutral-300 dark:border-white/20 before:content-['_'] after:content-['_']",
      },
    },
  }),
  Blockquote,
  HorizontalRule.configure({ HTMLAttributes: { class: "border-neutral-400/20" } }),
  DiffParagraph,
  YChange,
  Contents.extend({ content: forceHeading ? "(title block+)" : "block*" }).configure({
    highlightColor,
  }),
  Heading,
  Title,
  ...(disableLinkHandle ? [] : [LinkHandle]),
  CodeBlock.configure({ lowlight }),
  isSSR ? DisabledIframe : IframeEmbed,
  SplineEmbed,
  (disableLink ? DisabledLink : Link).configure({
    HTMLAttributes: {
      class: "text-community-primary no-underline hover:underline hover:cursor-pointer",
    },
    openOnClick: false,
  }),
  MemberInlineBlock,
  ToolInlineBlock,
  SpaceInlineBlock,
  ImageBlock.extend({
    addProseMirrorPlugins() {
      return [FileUploadPlugin()];
    },
  }),
  FileBlock,
  VideoBlock,
  GifBlock,
  OpenGraphBlock,
  ThreadBlock,
  ReplyBlock,
  CalloutBlock,
  TextColor,
  DocsGalleryBlock,
  Highlight.configure({ multicolor: true }),
  AccordionBlock,
  AccordionSummary,
  AccordionContent,
  TabsPanel,
  TabNode,
  HardBreak.configure({
    keepMarks: false,
  }),
  TableBlock.configure({
    resizable: true,
    lastColumnResizable: true,
    handleWidth: 6,
    cellMinWidth: 40,
    allowTableNodeSelection: true, // needs to be true to allow table to be dragged without duplicating the table: https://github.com/ueberdosis/tiptap/issues/2250
    HTMLAttributes: {
      class: "table-light dark:table-dark",
    },
  }),
  TableRow,
  TableCell,
  TableHeader,
  Markdown.configure({
    html: true, // Allow HTML input/output
    tightLists: true, // No <p> inside <li> in markdown output
    tightListClass: "tight", // Add class to <ul> allowing you to remove <p> margins when tight
    bulletListMarker: "-", // <li> prefix in markdown output
    linkify: false, // Create links from "https://..." text
    breaks: false, // New lines (\n) in markdown input are converted to <br>
    transformPastedText: false, // Allow to paste markdown text in the editor
    transformCopiedText: false, // Copied text is transformed to markdown
  }),
];

export const styles = [
  // headings
  "prose prose-sm prose-h1:text-[26px] focus:outline-none prose-h1:mt-12 prose-h2:mt-8 prose-h1:text-neutral-700 dark:prose-h1:text-white prose-h2:text-neutral-700",
  "prose-h1:scroll-mt-28 prose-h1:font-semibold prose-h2:font-semibold prose-h2:scroll-mt-28 prose-h3:scroll-mt-28 prose-h4:scroll-mt-28 prose-h5:scroll-mt-28",
  "dark:prose-h2:text-white prose-h3:text-neutral-700 dark:prose-h3:text-white prose-h4:text-neutral-700 dark:prose-h4:text-white",
  // general
  "flex-inline w-full h-full dark:prose-invert dark:prose-p:text-neutral-300/80 font-normal",
  // code blocks (other styles defined in CodeBlock.svelte)
  "prose-pre:px-4 prose-pre:py-3 prose-pre:text-[13px] prose-pre:leading-[1.6]",
  // inline code
  "prose-code:text-neutral-600 prose-code:font-normal dark:prose-code:text-neutral-300 prose-code:bg-black/5 dark:prose-code:border-white/5  dark:prose-code:bg-white/5 prose-code:rounded-sm",
  // paragraph
  "before:prose-p:content-none after:prose-p:content-none",
  // ensures empty paragraphs are the same height in ssr + csr
  "empty:prose-p:p-3",
  // blockquote
  "prose-blockquote:text-neutral-400 dark:prose-blockquote:text-neutral-300/80 prose-blockquote:not-italic",
].join(" ");
